import { useEffect, useState } from "react";
import type { PackingState } from "../domain/schema";
import {
	assignmentGroups,
	bagLabel,
	bagManifestText,
	packedQuantity,
	packerLabel,
	packingProblems,
	recipientLabel,
	remainingAssignmentText,
	updateAssignment,
	type AssignmentGroup,
	type AssignmentPatch,
} from "../domain/focus8-packing";
import { assignItemToBag } from "../domain/readiness";
import { currentRights } from "../domain/entitlement-snapshot";
import { platform } from "../platform/adapter";
import { mayLeave, useLeaveFence } from "./leave-fence";
import "./focus8-packing.css";
type Screen = "assign" | "pack" | "check" | "send";
function AssignmentEditor({
	group,
	state,
	busy,
	save,
	close,
}: {
	group: AssignmentGroup;
	state: PackingState;
	busy: boolean;
	save: (patch: AssignmentPatch) => Promise<boolean>;
	close: () => void;
}) {
	const item = group.items[0]!;
	const [value, setValue] = useState<AssignmentPatch>({
			name: group.name,
			recipientId: item.recipientId,
			ownerId: item.ownerId,
			bagId: item.assignedBagId,
			quantity: group.quantity,
			last: item.packMoment === "departure_morning",
		}),
		[error, setError] = useState("");
	const clear = useLeaveFence(
		JSON.stringify(value) !==
			JSON.stringify({
				name: group.name,
				recipientId: item.recipientId,
				ownerId: item.ownerId,
				bagId: item.assignedBagId,
				quantity: group.quantity,
				last: item.packMoment === "departure_morning",
			}),
		() => setError("담당 변경을 저장하거나 취소해 주세요."),
	);
	return (
		<form
			className="f8-cp-editor"
			onSubmit={async (e) => {
				e.preventDefault();
				try {
					if (await save(value)) {
						clear();
						close();
					} else setError("아직 저장하지 못했어요. 입력을 남겨 두었어요.");
				} catch (error) {
					setError(
						error instanceof Error ? error.message : "입력을 확인해 주세요.",
					);
				}
			}}
		>
			<h3>누구의 것을, 누가 챙길까요?</h3>
			<label>
				물건 이름
				<input
					value={value.name}
					onChange={(e) => setValue({ ...value, name: e.target.value })}
				/>
			</label>
			<div className="f8-cp-form-pair">
				<label>
					누구 것
					<select
						aria-label="누구 것"
						value={value.recipientId ?? ""}
						onChange={(e) =>
							setValue({ ...value, recipientId: e.target.value || undefined })
						}
					>
						<option value="">아직 미정</option>
						<option value="shared">함께 쓰는 것</option>
						{state.trip.participants?.map((person) => (
							<option value={person.id} key={person.id}>
								{person.name}
							</option>
						))}
					</select>
				</label>
				<label>
					챙길 사람
					<select
						aria-label="챙길 사람"
						value={value.ownerId}
						onChange={(e) => setValue({ ...value, ownerId: e.target.value })}
					>
						<option value="shared">아직 미정</option>
						{state.trip.ownerIds.map((name) => (
							<option key={name} value={name}>
								{name}
							</option>
						))}
					</select>
				</label>
			</div>
			<div className="f8-cp-form-pair">
				<label>
					가방
					<select
						aria-label="가방"
						value={value.bagId ?? ""}
						onChange={(e) =>
							setValue({ ...value, bagId: e.target.value || undefined })
						}
					>
						<option value="">아직 미정</option>
						{state.bags.map((bag) => (
							<option key={bag.id} value={bag.id}>
								{bag.name}
							</option>
						))}
					</select>
				</label>
				<label>
					전체 수량
					<input
						type="number"
						min="1"
						max={Math.max(30, group.quantity)}
						value={value.quantity}
						onChange={(e) =>
							setValue({ ...value, quantity: Number(e.target.value) })
						}
					/>
				</label>
			</div>
			<label className="f8-cp-check">
				<input
					type="checkbox"
					checked={value.last}
					onChange={(e) => setValue({ ...value, last: e.target.checked })}
				/>
				출발 아침에 챙길 것
			</label>
			<p>
				물건 주인은 이 기기에만 보관하며 가족 공유 링크에 동기화하지 않아요.
				이미 담은 {group.packed}개는 수량을 줄여 지우지 않아요. 가방 배정은 실제
				이동·반입 허가가 아닙니다.
			</p>
			{error && (
				<p role="alert" className="f8-cp-error">
					{error}
				</p>
			)}
			<div className="f8-cp-actions">
				<button type="submit" className="button primary" disabled={busy}>
					담당·수량·가방 저장
				</button>
				<button
					type="button"
					className="button secondary"
					disabled={busy}
					onClick={() => {
						clear();
						close();
					}}
				>
					취소
				</button>
			</div>
		</form>
	);
}
export function Focus8Packing({
	state,
	onChange,
	onTools,
	saved,
	demo = false,
}: {
	state: PackingState;
	onChange: (next: PackingState) => Promise<boolean>;
	onTools: (section: "checklist" | "people" | "baggage" | "departure") => void;
	saved: boolean;
	demo?: boolean;
}) {
	useEffect(() => {
		const previousTheme = document.documentElement.getAttribute("data-theme");
		document.documentElement.setAttribute("data-theme", "light");
		return () => {
			if (previousTheme === null)
				document.documentElement.removeAttribute("data-theme");
			else document.documentElement.setAttribute("data-theme", previousTheme);
		};
	}, []);
	const [screen, setScreen] = useState<Screen>("assign"),
		[view, setView] = useState<"people" | "bags">("people"),
		[editing, setEditing] = useState<string[]>([]),
		[busy, setBusy] = useState(false),
		[message, setMessage] = useState(""),
		[who, setWho] = useState("all"),
		[output, setOutput] = useState<"remaining" | "bags">("remaining");
	const groups = assignmentGroups(state),
		problems = packingProblems(state),
		packed = state.items.filter(
			(item) => item.checklistStatus === "packed",
		).length;
	const selected = editing.length
		? groups.find((group) =>
				group.items.some((item) => editing.includes(item.id)),
			)
		: undefined;
	const total = state.items.length,
		issues = problems.battery.inChecked.length;
	const save = async (next: PackingState) => {
		if (busy) return false;
		setBusy(true);
		try {
			const ok = await onChange({
				...next,
				trip: { ...next.trip, updatedAt: new Date().toISOString() },
			});
			if (ok)
				setMessage(
					"변경을 이 기기에 보관했어요. 실제 물건도 함께 확인해 주세요.",
				);
			return ok;
		} finally {
			setBusy(false);
		}
	};
	const show = (next: Screen) => {
		if (mayLeave()) {
			setScreen(next);
			setMessage("");
			document.querySelector("main")?.scrollTo({ top: 0 });
			window.scrollTo({ top: 0 });
		}
	};
	const text =
		output === "bags"
			? bagManifestText(state)
			: remainingAssignmentText(state, who);
	const row = (group: AssignmentGroup, packing = false) => {
		const item = group.items[0]!;
		return (
			<li key={group.key}>
				<div className="f8-cp-item">
					<small>
						{recipientLabel(state, item)}{" "}
						{item.packMoment === "departure_morning" && <span>출발 아침</span>}
					</small>
					<button
						type="button"
						className="f8-cp-item-name"
						onClick={() => {
							if (mayLeave()) setEditing(group.items.map((item) => item.id));
						}}
					>
						{group.name} <span aria-hidden="true">›</span>
					</button>
					<p>
						{packerLabel(item)} · {bagLabel(state, item)} ·{" "}
						{group.items.some((item) => item.weightG === undefined)
							? "무게 미측정"
							: `알려진 무게 ${group.items.reduce((sum, item) => sum + item.weightG!, 0).toLocaleString()}g`}
					</p>
					{group.items.some((item) =>
						problems.battery.inChecked.some((value) => value.id === item.id),
					) && (
						<p className="f8-cp-warning">△ 보조·여분 배터리 가방 배정 확인</p>
					)}
				</div>
				{packing ? (
					<div className="f8-cp-stepper">
						<button
							type="button"
							aria-label={`${group.name} 담은 수량 줄이기`}
							disabled={busy || group.packed === 0}
							onClick={() =>
								void save(
									packedQuantity(
										state,
										group.items.map((item) => item.id),
										group.packed - 1,
									),
								)
							}
						>
							−
						</button>
						<span aria-live="polite">
							<strong>{group.packed}</strong>/{group.quantity}
						</span>
						<button
							type="button"
							aria-label={`${group.name} 담은 수량 늘리기`}
							disabled={busy || group.packed === group.quantity}
							onClick={() =>
								void save(
									packedQuantity(
										state,
										group.items.map((item) => item.id),
										group.packed + 1,
									),
								)
							}
						>
							+
						</button>
					</div>
				) : (
					<button
						type="button"
						className="f8-cp-edit"
						onClick={() => {
							if (mayLeave()) setEditing(group.items.map((item) => item.id));
						}}
					>
						{item.ownerId === "shared" ? "맡기기" : "바꾸기"} →
					</button>
				)}
			</li>
		);
	};
	const groupKeys = [
		...new Set(
			groups.map((group) =>
				view === "people"
					? group.items[0]!.ownerId
					: (group.items[0]!.assignedBagId ?? ""),
			),
		),
	];
	return (
		<section className="f8-cp" aria-label="짐 분담과 수량 확인">
			<nav className="f8-cp-steps" aria-label="짐 준비 순서">
				{(
					[
						["assign", "나눠 맡기기"],
						["pack", "짐 싸기"],
						["check", "출발 확인"],
						["send", "목록 보내기"],
					] as const
				).map(([key, label], i) => (
					<button
						type="button"
						key={key}
						onClick={() => show(key)}
						aria-current={screen === key ? "step" : undefined}
					>
						<small>0{i + 1}</small>
						{label}
					</button>
				))}
			</nav>
			<div className="f8-cp-main">
				<div className="f8-cp-progress">
					<span>
						<strong>{packed}</strong>개 담음 <small>/ {total}개</small>
					</span>
					<span>
						남은 것 <strong>{total - packed}</strong>개
					</span>
				</div>
				{issues > 0 && (
					<p className="f8-cp-packed-note">
						담은 것 중 {issues}개는 배터리 가방 배정을 확인해 주세요.
					</p>
				)}
				{selected && (
					<AssignmentEditor
						key={selected.items.map((item) => item.id).join(":")}
						group={selected}
						state={state}
						busy={busy}
						save={(patch) =>
							save(updateAssignment(state, editing, patch, currentRights()))
						}
						close={() => setEditing([])}
					/>
				)}
				{screen === "assign" && (
					<>
						<header className="f8-cp-title">
							<p>내 물건과 내 할 일은 달라요</p>
							<h2>누가 챙길지부터 정해요.</h2>
							<p>
								누구의 물건인지 그대로 두고, 챙기는 사람과 넣을 가방을 정해요.
							</p>
						</header>
						{problems.unassigned.length > 0 && (
							<section className="f8-cp-unassigned">
								<header>
									<strong>아직 챙길 사람 미정</strong>
									<span>{problems.unassigned.length}개</span>
								</header>
								<ul>
									{groups
										.filter((group) => group.items[0]!.ownerId === "shared")
										.map((group) => row(group))}
								</ul>
							</section>
						)}
						<div className="f8-cp-legend">
							<span>물건 · 누구의 것 / 챙기는 사람 · 가방</span>
							<span>담당</span>
						</div>
						<ul className="f8-cp-assignment">
							{groups
								.filter((group) => group.items[0]!.ownerId !== "shared")
								.map((group) => row(group))}
						</ul>
						{!total && (
							<p className="f8-cp-empty">
								아직 물건이 없어요. 준비물 목록을 붙여 넣거나 직접 추가해
								주세요.
							</p>
						)}
						<div className="f8-cp-next">
							<button
								type="button"
								className="text-button"
								onClick={() => onTools("checklist")}
							>
								물건 추가·목록 붙여넣기
							</button>
							<button
								type="button"
								className="button primary"
								onClick={() => show("pack")}
							>
								짐 싸기 →
							</button>
						</div>
					</>
				)}
				{screen === "pack" && (
					<>
						<header className="f8-cp-title">
							<p>표시만 한 준비와, 실제 담은 수량</p>
							<h2>넣은 만큼만 눌러요.</h2>
							<p>
								준비 완료와 가방에 넣음은 별개예요. 수량은 0부터 전체 수량까지만
								바뀌어요.
							</p>
						</header>
						<div className="f8-cp-view" role="group" aria-label="짐 싸기 묶음">
							<button
								type="button"
								aria-pressed={view === "people"}
								onClick={() => setView("people")}
							>
								챙기는 사람별
							</button>
							<button
								type="button"
								aria-pressed={view === "bags"}
								onClick={() => setView("bags")}
							>
								가방별
							</button>
						</div>
						<div className="f8-cp-groups">
							{groupKeys.map((key) => (
								<section key={key}>
									<h3>
										{view === "people"
											? key === "shared"
												? "챙길 사람 미정"
												: key
											: (state.bags.find((bag) => bag.id === key)?.name ??
												"가방 미정")}
									</h3>
									<ul>
										{groups
											.filter(
												(group) =>
													(view === "people"
														? group.items[0]!.ownerId
														: (group.items[0]!.assignedBagId ?? "")) === key,
											)
											.map((group) => row(group, true))}
									</ul>
								</section>
							))}
						</div>
						<div className="f8-cp-next">
							<span>가방 배정은 실제 이동을 뜻하지 않아요.</span>
							<button
								type="button"
								className="button primary"
								onClick={() => show("check")}
							>
								출발 확인 →
							</button>
						</div>
					</>
				)}
				{screen === "check" && (
					<>
						<header className="f8-cp-title">
							<p>담은 수량과 남은 문제를 따로 확인해요</p>
							<h2>출발 전에, 한 번 더.</h2>
						</header>
						{problems.battery.inChecked.map((item) => (
							<article className="f8-cp-problem" key={item.id}>
								<h3>{item.name} · 가방 배정 확인</h3>
								<p>
									{bagLabel(state, item)}에 배정했어요. 보조·여분 배터리의 실제
									반입 조건은 운항사·공항의 최신 안내를 확인해 주세요.
								</p>
								{problems.cabinBags.map((bag) => (
									<button
										type="button"
										className="button secondary"
										disabled={busy}
										key={bag.id}
										onClick={() =>
											void save(assignItemToBag(state, item.id, bag.id))
										}
									>
										{bag.name}으로 배정
									</button>
								))}
							</article>
						))}
						{!issues && (
							<p className="f8-cp-check-note">
								위탁으로 배정된 보조·여분 배터리는 없어요. 항공 반입 허가를
								확인한 뜻은 아니에요.
							</p>
						)}
						{state.trip.departureChecks?.map((check) => (
							<label className="f8-cp-check" key={check.id}>
								<input
									type="checkbox"
									checked={check.checked}
									disabled={busy}
									onChange={(e) =>
										void save({
											...state,
											trip: {
												...state.trip,
												departureChecks: state.trip.departureChecks?.map(
													(row) =>
														row.id === check.id
															? {
																	...row,
																	checked: e.target.checked,
																	updatedAt: new Date().toISOString(),
																}
															: row,
												),
											},
										})
									}
								/>
								{check.label}
							</label>
						))}
						<p className="f8-cp-unknown">
							가방 미정 {problems.unbagged.length}개 · 챙길 사람 미정{" "}
							{problems.unassigned.length}개 · 무게 미측정{" "}
							{problems.unknownWeights.length}개 미측정 무게를 0으로 계산하거나
							가방 여유를 확정하지 않아요.
						</p>
						<button
							type="button"
							className="text-button"
							onClick={() => onTools("departure")}
						>
							배터리·액체·서류 상세 점검 열기
						</button>
						<div className="f8-cp-next">
							<span>체크 표시는 사용자가 직접 확인한 내용이에요.</span>
							<button
								type="button"
								className="button primary"
								onClick={() => show("send")}
							>
								남은 목록 만들기 →
							</button>
						</div>
					</>
				)}
				{screen === "send" && (
					<>
						<header className="f8-cp-title">
							<p>말로 다시 나누지 않도록</p>
							<h2>남은 것만 전해요.</h2>
						</header>
						<div className="f8-cp-view" role="group" aria-label="내보낼 목록">
							<button
								type="button"
								aria-pressed={output === "remaining"}
								onClick={() => setOutput("remaining")}
							>
								사람별 남은 것
							</button>
							<button
								type="button"
								aria-pressed={output === "bags"}
								onClick={() => setOutput("bags")}
							>
								가방 명세
							</button>
						</div>
						{output === "remaining" && (
							<label className="f8-cp-select">
								누가 챙길 목록인가요?
								<select value={who} onChange={(e) => setWho(e.target.value)}>
									<option value="all">전체</option>
									<option value="shared">챙길 사람 미정</option>
									{state.trip.ownerIds.map((name) => (
										<option value={name} key={name}>
											{name}
										</option>
									))}
								</select>
							</label>
						)}
						<article className="f8-cp-paper">
							<textarea
								aria-label="보낼 짐 목록"
								value={text}
								readOnly
								onFocus={(e) => e.target.select()}
							/>
						</article>
						<div className="f8-cp-actions">
							<button
								type="button"
								className="button primary"
								onClick={async () => {
									try {
										if (!navigator.clipboard?.writeText) throw new Error();
										await navigator.clipboard.writeText(text);
										setMessage(
											"목록을 복사했어요. 발송·읽음·공동 수정은 확인하지 않아요.",
										);
									} catch {
										setMessage(
											"자동 복사가 어려워요. 위 목록을 선택해 직접 복사해 주세요.",
										);
									}
								}}
							>
								목록 복사
							</button>
							<button
								type="button"
								className="button secondary"
								onClick={async () => {
									try {
										await platform.shareFile(
											new File([text], "carryonpan-packing-list.txt", {
												type: "text/plain;charset=utf-8",
											}),
										);
										setMessage(
											"목록 파일을 준비했어요. 실제 발송 여부는 직접 확인해 주세요.",
										);
									} catch {
										setMessage(
											"파일을 받지 못했어요. 위 목록을 선택해 복사할 수 있어요.",
										);
									}
								}}
							>
								글 파일 받기
							</button>
						</div>
						<p className="f8-cp-copy-limit">
							복사는 발송·읽음·공동 수정을 뜻하지 않아요. 새로운 물건 주인
							정보는 이 기기와 직접 받은 백업·글에만 담으며 기존 암호화 공유
							링크에는 추가하지 않아요.
						</p>
					</>
				)}
				<p className="f8-cp-status" role="status">
					{message}
				</p>
				<footer className="f8-cp-footer">
					{demo
						? "둘러보기 · 내 여행에는 저장되지 않음"
						: saved
							? "실제 기기 보관본과 연결됨"
							: "저장 확인 필요 · 아래에서 재시도·사본 받기"}
					<button
						type="button"
						className="text-button"
						onClick={() => onTools("people")}
					>
						동행 편집 ↗
					</button>
				</footer>
			</div>
		</section>
	);
}
